import { useState, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactDOM from 'react-dom';
import { Siren, Upload, Trash2, Pencil, Printer, Download, FileText, Sparkles, Eye, X, ClipboardList } from 'lucide-react';
import DarkSelect from '../components/DarkSelect.tsx';
import DatePicker from '../components/DatePicker.tsx';
import { api } from '../api/client.ts';
import { useAuth } from '../contexts/AuthContext';
import { useFacility, useFacilityLogoUrl } from '../contexts/FacilityContext.tsx';
import { type LogbookFormSchema, type LogbookFormField, gridFieldKey } from '../components/LogbookFormRenderer.tsx';
import '../styles/buttonAnimations.css';

// ─── Types ──────────────────────────────────────────────────────────────────

interface DisasterManualSection {
  id: number;
  facility_id: number;
  section_key: string;
  title: string;
  content: string;
  sort_order: number;
  updated_at: string;
  updated_by: number | null;
}

interface DisasterManualFile {
  id: number;
  facility_id: number;
  file_url: string | null;
  file_name: string | null;
  uploaded_at: string;
  uploaded_by: number | null;
}

export interface FilledEntry {
  id: number;
  template_id: number;
  work_order_id: number | null;
  facility_id: number;
  filled_data: Record<string, string | boolean>;
  filled_by: number | null;
  filled_at: string;
  updated_at: string | null;
  updated_by: number | null;
  created_at: string;
  template_name: string;
  department_id: number | null;
  form_schema: LogbookFormSchema | null;
  department_name: string | null;
  department_color: string | null;
  department_icon: string | null;
  wo_number: string | null;
  work_order_title: string | null;
  filled_by_first_name: string | null;
  filled_by_last_name: string | null;
  filled_by_username: string | null;
}
interface TemplateOption {
  id: number;
  name: string;
}
interface DepartmentOption {
  id: number;
  name: string;
}

// ─── Outer switcher — Disaster Manual / Filled Logs ────────────────────────
// pill-tab-sub is the existing shared sub-navigation pattern (buttonAnimations.css) already
// used for Compliance PM's Smart View/Timeline/Cards switcher in Tasks.tsx — reused verbatim,
// not a one-off, per BUTTON_SYSTEM.md.

type LogbooksSubView = 'disaster-manual' | 'filled-logs';

export default function Logbooks() {
  const [subView, setSubView] = useState<LogbooksSubView>('disaster-manual');

  return (
    <div>
      <div className="pill-tab-container pill-tab-sub" style={{ marginBottom: 20 }}>
        {([
          { id: 'disaster-manual', label: 'Disaster Manual' },
          { id: 'filled-logs', label: 'Filled Logs' },
        ] as { id: LogbooksSubView; label: string }[]).map((v) => (
          <button
            key={v.id}
            className={`pill-tab pill-tab-sub${subView === v.id ? ' active' : ''}`}
            onClick={() => setSubView(v.id)}
          >
            {v.label}
          </button>
        ))}
      </div>
      {subView === 'disaster-manual' ? <DisasterManualView /> : <FilledLogsShell />}
    </div>
  );
}

// ─── Disaster Manual ────────────────────────────────────────────────────────

function DisasterManualView() {
  const { user } = useAuth();
  const { facilityName } = useFacility();
  const qc = useQueryClient();
  // Same threshold fire-logbook.ts's AI Summary button uses client-side (Reports.tsx:1707),
  // widened to also include org_admin per this feature's explicit spec — admin/org_admin bypass
  // the tier requirement entirely, matching the server-side gate in disaster-manual.ts.
  const canEdit = user?.orgPlanTier === 'pro' || user?.orgPlanTier === 'enterprise' || user?.role === 'admin' || user?.role === 'org_admin';
  const canManageFile = user?.role !== 'viewer';

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiPromptFor, setAiPromptFor] = useState<number | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiRunning, setAiRunning] = useState(false);
  const [deleteFileConfirm, setDeleteFileConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: sectionsRes, isLoading: sectionsLoading } = useQuery({
    queryKey: ['disaster-manual-sections'],
    queryFn: () => api.get<DisasterManualSection[]>('/disaster-manual/sections'),
  });
  const sections: DisasterManualSection[] = sectionsRes?.data ?? [];

  const { data: fileRes } = useQuery({
    queryKey: ['disaster-manual-file'],
    queryFn: () => api.get<DisasterManualFile | null>('/disaster-manual/file'),
  });
  const file = fileRes?.data ?? null;

  function startEdit(s: DisasterManualSection) {
    setEditingId(s.id);
    setDraftTitle(s.title);
    setDraftContent(s.content);
    setAiPromptFor(null);
  }
  function cancelEdit() {
    setEditingId(null);
  }
  async function saveEdit(id: number) {
    setSaving(true);
    try {
      await api.put(`/disaster-manual/sections/${id}`, { title: draftTitle, content: draftContent });
      qc.invalidateQueries({ queryKey: ['disaster-manual-sections'] });
      setEditingId(null);
    } catch { /* silent — inline save, no toast infra on this view yet */ }
    setSaving(false);
  }

  async function runAiAssist(id: number) {
    if (!aiPrompt.trim()) return;
    setAiRunning(true);
    try {
      const res = await api.post<DisasterManualSection>(`/disaster-manual/sections/${id}/ai-assist`, { prompt: aiPrompt.trim() });
      qc.invalidateQueries({ queryKey: ['disaster-manual-sections'] });
      if (editingId === id) setDraftContent(res.data.content);
      setAiPromptFor(null);
      setAiPrompt('');
    } catch { /* silent */ }
    setAiRunning(false);
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      await api.postForm('/disaster-manual/file', fd);
      qc.invalidateQueries({ queryKey: ['disaster-manual-file'] });
    } catch { /* silent */ }
    e.target.value = '';
  }
  async function handleFileDelete() {
    try {
      await api.delete('/disaster-manual/file');
      qc.invalidateQueries({ queryKey: ['disaster-manual-file'] });
    } catch { /* silent */ }
    setDeleteFileConfirm(false);
  }

  return (
    <div>
      {/* Print-isolation CSS — same window.print() mechanism as Logbook Templates' "Print binder"
          button (client/src/pages/LogbookTemplates.tsx), scoped to this component only so it
          doesn't affect printing anywhere else in the app. Standard print-isolation technique:
          hide everything, then re-reveal only the print area. */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #disaster-manual-print-area, #disaster-manual-print-area * { visibility: visible; }
          #disaster-manual-print-area { position: absolute; top: 0; left: 0; width: 100%; padding: 24px; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(239,68,68,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Siren size={22} color="#f87171" />
        </div>
        <div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>Emergency &amp; Disaster Preparedness Manual</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{facilityName || 'This facility'}</div>
        </div>
        <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>
          <Printer size={14} /> Print manual
        </button>
      </div>

      {!canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px dashed rgba(139,92,246,0.3)', color: '#8b5cf6', fontSize: 12, marginBottom: 16 }} className="no-print">
          Section editing and AI assist require Pro or Enterprise. You can still view and print every section below.
        </div>
      )}

      {/* Attached reference file */}
      <div className="card no-print" style={{ padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
          Attached reference file
        </div>
        {file?.file_url ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 7, background: 'rgba(59,130,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#3b82f6' }}>
              <FileText size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.file_name || 'Reference file'}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>Uploaded {new Date(file.uploaded_at).toLocaleDateString()}</div>
            </div>
            <a className="btn btn-ghost btn-sm" href={file.file_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}><Download size={13} /> Download</a>
            {canManageFile && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={() => fileInputRef.current?.click()}><Upload size={13} /> Replace</button>
                <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }} onClick={() => setDeleteFileConfirm(true)}><Trash2 size={13} /> Delete</button>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No reference file uploaded.</span>
            {canManageFile && (
              <button className="btn btn-ghost btn-sm" onClick={() => fileInputRef.current?.click()}><Upload size={13} /> Upload</button>
            )}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleFileUpload} />
      </div>

      {/* Structured sections */}
      <div id="disaster-manual-print-area">
        {sectionsLoading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 12 }}>Loading…</div>
        ) : (
          sections.map((s) => (
            <div key={s.id} className="card" style={{ padding: '16px 18px', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                {editingId === s.id ? (
                  <input className="form-input no-print" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} style={{ flex: 1, fontWeight: 600 }} />
                ) : (
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, flex: 1 }}>{s.title}</div>
                )}
                {canEdit && editingId !== s.id && (
                  <button className="no-print" title="Edit" onClick={() => startEdit(s)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                    <Pencil size={13} />
                  </button>
                )}
              </div>

              {editingId === s.id ? (
                <div className="no-print">
                  <textarea
                    className="form-input"
                    value={draftContent}
                    onChange={(e) => setDraftContent(e.target.value)}
                    rows={6}
                    style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    <button
                      disabled={aiRunning}
                      onClick={() => setAiPromptFor(aiPromptFor === s.id ? null : s.id)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 7, border: 'none', background: '#7c3aed', color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                    >
                      <Sparkles size={12} /> ✦ AI assist
                    </button>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <button className="btn btn-ghost combo-cancel-jello" onClick={cancelEdit}>Cancel</button>
                      <button className="btn btn-primary combo2" disabled={saving} onClick={() => saveEdit(s.id)}>{saving ? 'Saving…' : 'Save'}</button>
                    </div>
                  </div>
                  {aiPromptFor === s.id && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <input
                        className="form-input"
                        placeholder='e.g. "reflect the new fire drill logged this month"'
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <button
                        disabled={aiRunning || !aiPrompt.trim()}
                        onClick={() => runAiAssist(s.id)}
                        style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: '#7c3aed', color: 'white', fontSize: 12, fontWeight: 600, cursor: aiRunning ? 'default' : 'pointer', opacity: aiRunning ? 0.7 : 1 }}
                      >
                        {aiRunning ? 'Generating…' : 'Generate'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: s.content ? 'var(--text-secondary)' : 'var(--text-muted)', whiteSpace: 'pre-wrap', fontStyle: s.content ? undefined : 'italic' }}>
                  {s.content || 'No content added yet.'}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {deleteFileConfirm && (
        <div className="modal-overlay no-print" onClick={(e) => e.target === e.currentTarget && setDeleteFileConfirm(false)}>
          <div className="modal" style={{ maxWidth: 380, width: '90vw' }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Reference File?</h3>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>The attached reference file will be removed. This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteFileConfirm(false)}>Cancel</button>
              <button className="btn" style={{ background: '#ef4444', color: '#fff' }} onClick={handleFileDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Filled Logs — real archive (Phase 3) ──────────────────────────────────
// Sourced from logbook_filled_entries (migration_v111.sql). Department-gated the same way as
// Phase 1's Logbook Templates workspace: a department user sees only their department's entries
// (via the entry's template's department_id) plus facility-wide ones; admin/org_admin see all —
// enforced server-side in logbook-filled-entries.ts, not just hidden client-side.

export function fmtFieldValue(v: string | boolean | undefined, type: LogbookFormField['type']): string {
  if (v === undefined || v === '' || v === null) return '—';
  if (type === 'checkbox') return v ? 'Yes' : 'No';
  return String(v);
}

// entry.form_schema is the template's LogbookFormSchema (single_form or grid) as of migration
// v115 -- NOT a flat FormField[] the way it was before that migration wrapped
// logbook_templates.form_schema into the {form_mode, ...} shape. Flattens either mode into a
// single label/value list for the (label, value) display both FilledEntryFields and
// FilledEntryPrintDoc render -- grid rows are prefixed with their row label so the flattened
// list stays unambiguous (a grid entry has the same row_field repeated once per row).
function extractFilledEntryRows(entry: FilledEntry): { key: string; label: string; value: string }[] {
  const schema = entry.form_schema;
  if (!schema) return [];
  if (schema.form_mode === 'single_form') {
    return (schema.fixed_fields ?? []).map((f) => ({
      key: f.key,
      label: f.label,
      value: fmtFieldValue(entry.filled_data[f.key], f.type),
    }));
  }
  const rows: { key: string; label: string; value: string }[] = [];
  for (const row of schema.rows ?? []) {
    for (const f of schema.row_fields ?? []) {
      rows.push({
        key: gridFieldKey(row.row_id, f.key),
        label: `${row.label} — ${f.label}`,
        value: fmtFieldValue(entry.filled_data[gridFieldKey(row.row_id, f.key)], f.type),
      });
    }
  }
  return rows;
}

function FilledLogsShell() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'org_admin';
  const logoUrl = useFacilityLogoUrl();
  const [templateFilter, setTemplateFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewing, setViewing] = useState<FilledEntry | null>(null);

  const { data: templatesRes } = useQuery({
    queryKey: ['logbook-templates'],
    queryFn: () => api.get<TemplateOption[]>('/logbook-templates'),
  });
  const templates: TemplateOption[] = templatesRes?.data ?? [];

  const { data: deptsRes } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<DepartmentOption[]>('/departments'),
    enabled: isAdmin,
  });
  const departments: DepartmentOption[] = deptsRes?.data ?? [];

  const { data: entriesRes, isLoading } = useQuery({
    queryKey: ['logbook-filled-entries', templateFilter, departmentFilter, dateFrom, dateTo],
    queryFn: () => api.get<FilledEntry[]>('/logbook-filled-entries', {
      ...(templateFilter ? { template_id: templateFilter } : {}),
      ...(isAdmin && departmentFilter ? { department_id: departmentFilter } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    }),
  });
  const entries: FilledEntry[] = entriesRes?.data ?? [];

  function filledByName(e: FilledEntry): string {
    if (e.filled_by_first_name || e.filled_by_last_name) return `${e.filled_by_first_name ?? ''} ${e.filled_by_last_name ?? ''}`.trim();
    return e.filled_by_username ?? 'Unknown';
  }

  function printEntry(e: FilledEntry) {
    setViewing(e);
    setTimeout(() => window.print(), 50);
  }

  return (
    <div>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #filled-entry-print-area, #filled-entry-print-area * { visibility: visible; }
          #filled-entry-print-area { display: block !important; position: absolute; top: 0; left: 0; width: 100%; padding: 24px; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ minWidth: 180 }}>
          <DarkSelect
            value={templateFilter}
            onChange={setTemplateFilter}
            placeholder="All templates"
            options={[{ value: '', label: 'All templates' }, ...templates.map((t) => ({ value: String(t.id), label: t.name }))]}
          />
        </div>
        {isAdmin && (
          <div style={{ minWidth: 160 }}>
            <DarkSelect
              value={departmentFilter}
              onChange={setDepartmentFilter}
              placeholder="All departments"
              options={[{ value: '', label: 'All departments' }, ...departments.map((d) => ({ value: String(d.id), label: d.name }))]}
            />
          </div>
        )}
        <div style={{ width: 150 }}>
          <DatePicker value={dateFrom} onChange={(v) => setDateFrom(v)} placeholder="From date" />
        </div>
        <div style={{ width: 150 }}>
          <DatePicker value={dateTo} onChange={(v) => setDateTo(v)} placeholder="To date" />
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 12 }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, opacity: 0.25, marginBottom: 14 }}>📋</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>No filled logs yet</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 360 }}>
            Filled logs appear here once a logbook form is attached to a Work Order and filled in (Tasks → Task Manager → New Work Order → Logbook Form).
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                {['Template', 'Department', 'Work Order', 'Filled By', 'Filled At', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: 12, fontWeight: 600, fontSize: '0.875rem' }}>{e.template_name}</td>
                  <td style={{ padding: 12 }}>
                    {e.department_id === null
                      ? <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Facility-wide</span>
                      : <span style={deptBadgeStyle(e.department_color ?? '#6b7280')}>{e.department_name}</span>}
                  </td>
                  <td style={{ padding: 12, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{e.wo_number ? `${e.wo_number} — ${e.work_order_title}` : '—'}</td>
                  <td style={{ padding: 12, fontSize: '0.8rem' }}>{filledByName(e)}</td>
                  <td style={{ padding: 12, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{new Date(e.filled_at).toLocaleString()}</td>
                  <td style={{ padding: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setViewing(e)} title="View" style={{ background: 'none', border: '1px solid var(--border-default)', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: 'var(--text-muted)', marginRight: 6 }}><Eye size={13} /></button>
                    <button onClick={() => printEntry(e)} title="Print" style={{ background: 'none', border: '1px solid var(--border-default)', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: 'var(--text-muted)' }}><Printer size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && ReactDOM.createPortal(
        <div className="modal-overlay no-print" onClick={(e) => e.target === e.currentTarget && setViewing(null)}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3 className="modal-title">{viewing.template_name}</h3>
              <button className="modal-close" onClick={() => setViewing(null)}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className="btn btn-ghost" onClick={() => window.print()}><Printer size={14} /> Print</button>
            </div>
            <FilledEntryFields entry={viewing} />
          </div>
        </div>,
        document.body
      )}

      {/* Print target — always rendered (off-screen unless printing) so window.print() from the
          table row's Printer icon works without first opening the view modal. */}
      {viewing && (
        <div id="filled-entry-print-area" style={{ display: 'none' }} className="print-only-render">
          <FilledEntryPrintDoc entry={viewing} logoUrl={logoUrl} />
        </div>
      )}
    </div>
  );
}

function deptBadgeStyle(color: string): React.CSSProperties {
  return { fontSize: 10, padding: '2px 8px', borderRadius: 20, background: color + '22', border: `1px solid ${color}66`, color, whiteSpace: 'nowrap' };
}

function FilledEntryFields({ entry }: { entry: FilledEntry }) {
  const rows = extractFilledEntryRows(entry);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((r) => (
        <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: '0.85rem', paddingBottom: 6, borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ color: 'var(--text-muted)' }}>{r.label}</span>
          <span style={{ fontWeight: 500, textAlign: 'right' }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

// Print rendering — field label + value pairs is sufficient for MVP per instruction; doesn't
// replicate an original uploaded file pixel-for-pixel.
export function FilledEntryPrintDoc({ entry, logoUrl }: { entry: FilledEntry; logoUrl?: string | null }) {
  const rows = extractFilledEntryRows(entry);
  return (
    <div>
      {logoUrl && (
        <img src={logoUrl} alt="" style={{ maxHeight: 56, maxWidth: 220, objectFit: 'contain', marginBottom: 12, display: 'block' }} />
      )}
      <h2 style={{ margin: '0 0 4px' }}>{entry.template_name}</h2>
      <p style={{ color: '#555', marginBottom: 20 }}>
        Filled {new Date(entry.filled_at).toLocaleString()}
        {entry.wo_number ? ` · ${entry.wo_number} — ${entry.work_order_title}` : ''}
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td style={{ padding: '8px 10px', border: '1px solid #ccc', fontWeight: 600, width: '40%' }}>{r.label}</td>
              <td style={{ padding: '8px 10px', border: '1px solid #ccc' }}>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Standalone-document renderer used by the PDF/print path (iframe document, not the live React
// tree, so it needs real HTML/CSS strings rather than JSX -- FilledEntryPrintDoc's JSX above stays
// as the plain flat table it always was; it's only used by Logbooks.tsx's own on-screen "View"
// modal preview, which is untouched by this pass).
//
// Section-card field layout below is copied from LogbookTemplates.tsx's TEMPLATE_PRINT_CSS (the
// blank-template PDF redesign) -- same per-file CSS duplication convention already used elsewhere
// in this app (e.g. the print-isolation CSS block duplicated across DisasterManualView and
// FilledLogsShell above) rather than a cross-file import, so this stays fully contained to this
// file. Only the classes this view actually uses are copied -- header-band/meta-row/instructions/
// doc-footer/option-grid from the blank-template version aren't relevant here: this view keeps its
// existing simple logo+title+subtitle header (unchanged, per instruction), and a filled entry never
// renders the room-number circle-grid -- that only makes sense for a blank, not-yet-filled field.
const FILLED_ENTRY_PRINT_CSS = `
  * { box-sizing: border-box; }
  @page { margin: 14mm 12mm; }
  body { font-family: 'Inter','Helvetica Neue',Arial,sans-serif; color: #1a2233; background: #fff; margin: 0; padding: 0; }
  .sheet { padding: 34px 40px 28px; background: #fff; }
  h2 { margin: 0 0 4px; font-size: 1.2rem; }
  .subtitle { color: #555; margin: 0 0 20px; font-size: 0.85rem; }

  .section-card { border: 1px solid #ddd; border-radius: 8px; padding: 10px 14px; margin-bottom: 6px; page-break-inside: avoid; }
  .section-header { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #0ea5e9; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid rgba(14,165,233,0.35); }
  .field-group { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 14px; }
  .field-group:last-child { margin-bottom: 0; }
  .field-block { flex: 1 1 150px; min-width: 130px; }
  .field-label { font-size: 0.66rem; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 3px; }
  .field-value { font-size: 0.85rem; border-bottom: 1px solid #ccc; padding-bottom: 3px; min-height: 16px; word-break: break-word; }

  table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  td { padding: 8px 10px; border: 1px solid #ccc; vertical-align: top; color: #1a2233; }
  td.label { font-weight: 600; width: 40%; }
`;

// One field's filled block -- same .field-block/.field-label/.field-value structure as the blank
// template's fieldBlockHtml, but shows the actual submitted value instead of a blank line, and
// never the room-number circle-grid (that grid is a fill-in aid for an unfilled form; a filled
// entry already has a real value to show, so it always renders as a plain field-value block).
function filledFieldBlockHtml(label: string, required: boolean | undefined, value: string, esc: (s: string) => string): string {
  return `<div class="field-block">
    <div class="field-label">${esc(label)}${required ? ' *' : ''}</div>
    <div class="field-value">${esc(value)}</div>
  </div>`;
}

// Same contiguous-run-by-section_id grouping LogbookTemplates.tsx's buildSingleFormHtml uses for
// the blank template (and LogbookFormRenderer.tsx's SingleFormBody uses for the live fill view) --
// duplicated here per this file's existing per-file-duplication convention (see CSS comment above)
// rather than imported, so a section-less field run renders as a bare unlabeled group and a run
// with a section_id becomes a labeled section-card, matching what filling out the form looked like.
function buildFilledSingleFormHtml(schema: LogbookFormSchema, entry: FilledEntry, esc: (s: string) => string): string {
  const fields = schema.fixed_fields ?? [];
  const sections = schema.sections ?? [];
  const blockFor = (f: LogbookFormField) => filledFieldBlockHtml(f.label, f.required, fmtFieldValue(entry.filled_data[f.key], f.type), esc);
  if (sections.length === 0) {
    return `<div class="field-group">${fields.map(blockFor).join('')}</div>`;
  }
  const labelBySection = new Map(sections.map(s => [s.section_id, s.label]));
  const segments: { sectionId?: string; fields: LogbookFormField[] }[] = [];
  for (const f of fields) {
    const last = segments[segments.length - 1];
    if (last && last.sectionId === f.section_id) last.fields.push(f);
    else segments.push({ sectionId: f.section_id, fields: [f] });
  }
  return segments.map((seg) => seg.sectionId
    ? `<div class="section-card">
        <div class="section-header">${esc(labelBySection.get(seg.sectionId) ?? 'Section')}</div>
        <div class="field-group">${seg.fields.map(blockFor).join('')}</div>
      </div>`
    : `<div class="field-group">${seg.fields.map(blockFor).join('')}</div>`
  ).join('');
}

function buildFilledEntryPrintHtml(entry: FilledEntry, logoUrl?: string | null): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const schema = entry.form_schema;
  const metaSuffix = entry.wo_number ? ` &middot; ${esc(entry.wo_number)} &mdash; ${esc(entry.work_order_title ?? '')}` : '';
  // single_form -> section-card grouped fields with real values (the redesign this pass adds).
  // grid mode is unchanged from before -- it never had sections in its schema to group by, so it
  // keeps the same flat label/value table it always used.
  const bodyContent = schema && schema.form_mode === 'single_form'
    ? buildFilledSingleFormHtml(schema, entry, esc)
    : `<table>
        <tbody>
          ${extractFilledEntryRows(entry).map((r) => `<tr><td class="label">${esc(r.label)}</td><td>${esc(r.value)}</td></tr>`).join('')}
        </tbody>
      </table>`;
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(entry.template_name)}</title>
<style>${FILLED_ENTRY_PRINT_CSS}</style>
</head>
<body>
<div class="sheet">
  ${logoUrl ? `<img src="${esc(logoUrl)}" alt="" crossorigin="anonymous" style="max-height:56px;max-width:220px;object-fit:contain;margin-bottom:12px;display:block" />` : ''}
  <h2>${esc(entry.template_name)}</h2>
  <p class="subtitle">Filled ${esc(new Date(entry.filled_at).toLocaleString())}${metaSuffix}</p>
  ${bodyContent}
</div>
</body>
</html>`;
}

// Generates a real paginated PDF of a filled entry instead of relying on window.print() against
// the live DOM -- same fix pattern (and same iframe/html2canvas/jsPDF technique) already applied
// to the blank-template print/download in LogbookTemplates.tsx. Filling out a form inside a WO's
// Checklist tab put this content behind ancestor panels with fixed height + overflow (the tab
// panel scroll area), so window.print()'s live-DOM path was clipping the printed output to
// whatever fit inside that overflow box -- the browser's print dialog still reported "1 of 1"
// because nothing beyond the clip was ever in the print layout to paginate in the first place.
// Rendering into an isolated off-screen iframe sidesteps any ancestor's overflow/height entirely.
export async function buildFilledEntryPdf(entry: FilledEntry, logoUrl?: string | null) {
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed; left:-99999px; top:0; width:816px; height:1056px; border:0;';
  document.body.appendChild(iframe);

  try {
    await new Promise<void>((resolve) => {
      iframe.onload = () => resolve();
      iframe.srcdoc = buildFilledEntryPrintHtml(entry, logoUrl);
    });
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('Print document unavailable');

    const images = Array.from(doc.querySelectorAll('img'));
    await Promise.all(images.map((img) => img.complete
      ? Promise.resolve()
      : new Promise<void>((resolve) => { img.onload = () => resolve(); img.onerror = () => resolve(); })));

    iframe.style.height = `${doc.body.scrollHeight}px`;

    const canvas = await html2canvas(doc.body, { backgroundColor: '#ffffff', scale: 2 });
    const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png');

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    const ROUNDING_TOLERANCE_PT = 1;
    while (heightLeft > ROUNDING_TOLERANCE_PT) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    return pdf;
  } finally {
    document.body.removeChild(iframe);
  }
}

// Loads the generated PDF (buildFilledEntryPdf) into a hidden iframe via a blob URL and triggers
// the browser's native print dialog on it -- printing an actual PDF sidesteps the browser's own
// page header/footer chrome (URL/title/"1 of N"/timestamp), which only gets injected into HTML
// print jobs, not PDF-viewer print jobs.
export async function printFilledEntryPdf(entry: FilledEntry, logoUrl?: string | null) {
  const pdf = await buildFilledEntryPdf(entry, logoUrl);
  const blobUrl = URL.createObjectURL(pdf.output('blob'));

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed; left:-99999px; top:0; width:0; height:0; border:0;';
  // src MUST be set before the iframe is inserted into the DOM -- appending first (as this used
  // to) makes the iframe initialize to about:blank and fire onload immediately (print dialog #1,
  // blank), then fire onload again once the blob src navigation completes (print dialog #2, the
  // real PDF). Setting src first means only the blob navigation ever happens, so onload fires once.
  // Same fix already applied to printTemplateBlankPdf in LogbookTemplates.tsx.
  iframe.onload = () => iframe.contentWindow?.print();
  iframe.src = blobUrl;
  document.body.appendChild(iframe);

  setTimeout(() => {
    document.body.removeChild(iframe);
    URL.revokeObjectURL(blobUrl);
  }, 60000);
}
